"use client";

// src/context/MenuContext.js
import { createContext, useContext, useState } from "react";

const MenuContext = createContext();

export const useMenu = () => useContext(MenuContext);

export function MenuProvider({ children }) {
  // Cart and Wishlist Counts
  const [cartCount, setCartCount] = useState(0);
  const [wishlistCount, setWishlistCount] = useState(0);

  // Coupon-related state
  const [promoCode, setPromoCode] = useState("");
  const [promoDiscount, setPromoDiscount] = useState(0);
  const [finalAmount, setFinalAmount] = useState(0);
  const [promoMessage, setPromoMessage] = useState("");

  // ✅ Shipping Charge
  const [shippingCharge, setShippingCharge] = useState(0);
  const [shippingError, setShippingError] = useState(null);

  // ✅ Menu Data
  const [menuItems, setMenuItems] = useState([]);
  const [menuSettings, setSettingsItems] = useState([]);
  const [selectedMain, setSelectedMain] = useState(null);
  const [selectedSub, setSelectedSub] = useState(null);
  const [selectedChild, setSelectedChild] = useState(null);
  const [menuLoading, setMenuLoading] = useState(false);
  const [menuError, setMenuError] = useState(null);
  const [logoUrl, setLogoUrl] = useState(null);
  const user = JSON.parse(localStorage.getItem("authUserId"));
  const id = user;

  // ✅ Helper: Get auth / guest token
  const getAuthData = () => {
    return {
      authToken: localStorage.getItem("authToken") || null,
      guestToken: localStorage.getItem("guest_token") || null,
      userId: localStorage.getItem("authUserId") || null,
    };
  };

  // ✅ Fetch Menu Items (Products/Categories etc.)
  const fetchMenuItems = async () => {
    const authData  = getAuthData();
    const token = authData.authToken || authData.guestToken;
    setMenuLoading(true);
    try {
        const res = await fetch(
          `https://tyka.premierhostings.com/beta/api/menu${
            id ? `?id=${id}` : ""
          }`,
          {
            headers: {
              "Content-Type": "application/json",
              ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
          }
        );

      if (!res.ok)
        throw new Error(`Failed to fetch menu, status: ${res.status}`);

      const data = await res.json();
      const items = data.data || [];
      if (data.settings?.logo) {
        setSettingsItems(data.settings);
        setLogoUrl(data.settings.logo);
      }

      setMenuError(null);
      setMenuItems(items);

      if (items.length) {
          const main = items[0];
          const sub = main.subcategories?.[0] || null;
          const child = sub?.children?.[0] || null;

          setSelectedMain(main);
          setSelectedSub(sub);
          setSelectedChild(child);
      }

      setWishlistCount(data.wishlist || 0);

      if (id) {
        setCartCount(typeof data.cart === "number" ? data.cart : 0);
      }
    } catch (err) {
      console.error("Menu fetch error:", err);
      setMenuError(err.message);
    } finally {
      setMenuLoading(false);
    }
  };

  const selectMain = (main) => {
      setSelectedMain(main);

      const sub = main.subcategories?.[0] || null;
      const child = sub?.children?.[0] || null;

      setSelectedSub(sub);
      setSelectedChild(child);
  };

  const selectSub = (sub) => {
      setSelectedSub(sub);

      const child = sub.children?.[0] || null;
      setSelectedChild(child);
  };

  const selectChild = (child) => {
      setSelectedChild(child);
  };
  // ✅ Fetch Shipping Charge
  const fetchShippingCharge = async () => {
    try {
      const res = await fetch(
        "https://tyka.premierhostings.com/beta/api/shipping-charge",
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);

      const data = await res.json();
      const charge = parseFloat(data.shipping_charge);

      if (!isNaN(charge)) {
        setShippingCharge(charge);
        setShippingError(null);
      } else {
        throw new Error("Invalid shipping charge from API");
      }
    } catch (err) {
      console.error("Shipping charge fetch error:", err);
      setShippingError(err.message);
      setShippingCharge(0);
    }
  };

  // ✅ Fetch Cart and Wishlist Counts (Login + Guest)
  // const fetchMenuCounts = async () => {
  //   try {
  //     const { authToken, guestToken } = getAuthData();

  //     let headers = { "Content-Type": "application/json" };
  //     if (authToken) headers.Authorization = `Bearer ${authToken}`;

  //     // 🔹 Fetch Cart Count
  //     const cartRes = await fetch(
  //       `https://tyka.premierhostings.com/beta/api/cart/count${
  //         guestToken ? `?guest_token=${guestToken}` : ""
  //       }`,
  //       { headers }
  //     );
  //     const cartData = await cartRes.json();

  //     // 🔹 Fetch Wishlist Count
  //     const wishRes = await fetch(
  //       `https://tyka.premierhostings.com/beta/api/wishlist/count${
  //         guestToken ? `?guest_token=${guestToken}` : ""
  //       }`,
  //       { headers }
  //     );
  //     const wishData = await wishRes.json();
  //     console.log("fdxgnkjc", cartData);
  //     debugger;
  //     // 🔹 Update State
  //     setCartCount(cartData.count || 0);
  //     setWishlistCount(wishData.count || 0);
  //   } catch (err) {
  //     console.error("Menu count fetch error:", err);
  //   }
  // };

  // ✅ Fetch Cart and Wishlist Counts
  const fetchMenuCounts = async (id, token) => {
    try {
      const res = await fetch(
        `https://tyka.premierhostings.com/beta/api/menu${
          id ? `?id=${id}` : ""
        }`,
        {
          headers: {
            "Content-Type": "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
        }
      );

      if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);
      const data = await res.json();

      setWishlistCount(data.wishlist || 0);

      if (id) {
        setCartCount(typeof data.cart === "number" ? data.cart : 0);
      }
      // setCartCount(typeof data.cart === "number" ? data.cart : 0);
    } catch (err) {
      console.error("Menu count fetch error:", err);
    }
  };

  return (
    <MenuContext.Provider
      value={{
        // Cart & Wishlist
        cartCount,
        wishlistCount,
        setCartCount,
        setWishlistCount,
        fetchMenuCounts,

        // Coupon values
        promoCode,
        setPromoCode,
        promoDiscount,
        setPromoDiscount,
        finalAmount,
        setFinalAmount,
        promoMessage,
        setPromoMessage,

        // ✅ Shipping values
        shippingCharge,
        setShippingCharge,
        fetchShippingCharge,
        shippingError,

        // ✅ Menu values
        menuItems,
        fetchMenuItems,
        menuSettings,
        logoUrl,
        selectedMain,
        selectedSub,
        selectedChild,

        selectMain,
        selectSub,
        selectChild,
        menuLoading,
        menuError,
      }}
    >
      {children}
    </MenuContext.Provider>
  );
}
